import React, { FC } from "react";
import { useSelector } from "react-redux";
import { hasEditableAccess } from "../../utils/permissions";
import { RootState } from "../../redux/reducers";

interface PermissionGuardProps {
  permissionKey: string;
  fallback?: React.ReactNode;
  children: React.ReactNode;
  hide?: boolean; // If true, hide children; if false, disable them
}

/**
 * PermissionGuard Component
 *
 * Usage 1 - Hide children (for edit buttons):
 * <PermissionGuard permissionKey="regularSchedule" hide>
 *   <button>Edit</button>
 * </PermissionGuard>
 *
 * Usage 2 - Disable children (for form inputs):
 * <PermissionGuard permissionKey="regularSchedule" hide={false}>
 *   <input type="text" />
 * </PermissionGuard>
 */
export const PermissionGuard: FC<PermissionGuardProps> = ({
  permissionKey,
  fallback = null,
  children,
  hide = true,
}) => {
  const user = useSelector((state: RootState) => state.user);
  const canEdit = hasEditableAccess(user, permissionKey);

  // ADMIN role always has full access
  if (user?.role === "ADMIN") {
    return <>{children}</>;
  }

  // If user can edit, render children normally
  if (canEdit) {
    return <>{children}</>;
  }

  // User doesn't have edit permission
  if (hide) {
    // Hide the children completely
    return <>{fallback}</>;
  } else {
    // Disable the children
    return (
      <div style={{ opacity: 0.6, pointerEvents: "none" }} aria-disabled="true">
        {children}
      </div>
    );
  }
};

export default PermissionGuard;
